import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainScreen } from '@/components/shared/MainScreen/MainScreen';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button, Card, CardContent, Spinner } from '@/components/ui';
import { useProjectViews } from '@/hooks/useProjectViews';
import { useAuthStore } from '@/stores/authStore';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import { ProjectsListView } from './components/ProjectsList';
import { TasksKanbanView } from './components/TasksKanban';

export default function DashboardPage() {
  const navigate = useNavigate();
  const query = useProjectViews();
  const [tab, setTab] = useState('projects');
  const isClientView = useAuthStore((state) => state.user?.role === 'client');
  const { emptyStateClientText, emptyStateWorkerText } = useDaoConfig();
  const newProject = () => navigate('/projects/new');
  return (
    <MainScreen
      headerContent={
        <div className="headerTitle flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <h1>Dashboard</h1>
          <div className="flex items-center gap-4">
            {!query.isPending && !query.isError && !!query.data.length && (
              <button
                type="button"
                disabled
                title="Project documentation is not available yet."
                className="inline-flex items-center gap-1 text-sm font-medium text-[--state-brand-active]"
              >
                Learn more about projects{' '}
                <i aria-hidden="true" className="ri-arrow-right-s-line" />
              </button>
            )}
            {isClientView && (
              <Button className="h-11 gap-2 px-4 text-lg" onClick={newProject}>
                <i aria-hidden="true" className="ri-add-large-line" /> New
                Project
              </Button>
            )}
          </div>
        </div>
      }
      tabs={
        isClientView
          ? undefined
          : [
              { id: 'projects', label: 'Projects' },
              { id: 'tasks', label: 'Tasks' },
            ]
      }
      activeTab={isClientView ? undefined : tab}
      onTabChange={isClientView ? undefined : setTab}
      contentClassName="pageContent text-left"
    >
      {query.isPending ? (
        <div
          role="status"
          aria-label="Loading dashboard"
          className="flex items-center justify-center py-24"
        >
          <div className="flex flex-col items-center gap-4">
            <Spinner size="lg" />{' '}
            <p className="text-[--text-dark-secondary]">Loading dashboard...</p>
          </div>
        </div>
      ) : query.isError ? (
        <Card className="mx-auto mt-6 max-w-2xl">
          <CardContent role="alert" className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/15">
              <i
                aria-hidden="true"
                className="ri-error-warning-line text-2xl text-red-400"
              />
            </div>
            <h2 className="mb-2 text-xl font-semibold">
              Failed to load dashboard
            </h2>
            <p className="mb-4 text-[--text-dark-secondary]">
              {query.error.message}
            </p>
            <Button variant="outline" onClick={() => void query.refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : !query.data.length ? (
        <div className="mt-6 text-center">
          <EmptyState
            icon="ri-folder-line"
            title="No projects yet"
            description={
              isClientView
                ? emptyStateClientText
                : `You have not been assigned to any projects yet. ${emptyStateWorkerText}`
            }
            action={
              isClientView
                ? { label: 'New Project', onClick: newProject }
                : undefined
            }
          />
        </div>
      ) : !isClientView && tab === 'tasks' ? (
        <TasksKanbanView
          projects={query.data}
          onNavigate={(id) => navigate('/projects/' + id)}
        />
      ) : (
        <ProjectsListView
          projects={query.data}
          isClient={isClientView}
          onProjectClick={(id) => navigate('/projects/' + id)}
        />
      )}
    </MainScreen>
  );
}
